import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge, type Tone } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Page, PageHeader, Section } from '@/components/ui/Page'
import { EmptyState, ErrorState, Loading } from '@/components/ui/States'
import { NewDocument } from '@/features/documents/NewDocument'
import { formatDateShort } from '@/lib/dates'
import { pluralise } from '@/lib/format'
import { QUESTIONNAIRE_STATUS_LABEL, questionnaireStatus, type QuestionnaireStatus } from '@/lib/questionnaire'
import { describeError } from '@/lib/supabase'
import {
  clientName,
  questionnairePdfUrl,
  useSentQuestionnaires,
  type SentQuestionnaire,
} from './queries'

export const STATUS_TONE: Record<QuestionnaireStatus, Tone> = {
  not_sent: 'neutral',
  sent: 'amber',
  opened: 'amber',
  started: 'amber',
  completed: 'sage',
}

/** Opens a fresh PDF of the questionnaire in a new tab. */
export async function openQuestionnairePdf(id: string) {
  // Open the tab first, while the click still counts, so it isn't blocked.
  const tab = window.open('', '_blank')
  try {
    const url = await questionnairePdfUrl(id)
    if (tab) tab.location.href = url
    else window.location.href = url
  } catch (error) {
    tab?.close()
    toast.error((error as Error).message)
  }
}

/**
 * Documents › Questionnaires (29 Oct 2026): every questionnaire made for a
 * client, when it was sent and whether they have completed it. "New
 * questionnaire" makes one for a project from a template, then its page has
 * the buttons to send it. Templates are in Settings › Templates.
 */
export function QuestionnairesPage() {
  const sent = useSentQuestionnaires()
  const [params, setParams] = useSearchParams()
  const forJob = params.get('new')
  const [adding, setAdding] = useState(forJob !== null)

  const counts = useMemo(() => {
    const rows = sent.data ?? []
    return { all: rows.length, waiting: rows.filter((row) => row.sent_at && !row.completed_at).length }
  }, [sent.data])

  return (
    <Page>
      <PageHeader
        eyebrow="Documents"
        title="Questionnaires"
        meta={
          sent.data ? (
            <>
              <span>{pluralise(counts.all, 'questionnaire')}</span>
              {counts.waiting ? <span className="text-amber">{counts.waiting} waiting for answers</span> : null}
            </>
          ) : null
        }
        actions={
          <Button variant="primary" onClick={() => setAdding(true)}>
            New questionnaire
          </Button>
        }
      />

      {adding ? (
        <NewDocument
          kind="questionnaire"
          jobId={forJob}
          onClose={() => {
            setAdding(false)
            if (forJob) setParams({}, { replace: true })
          }}
        />
      ) : null}

      <Section>
        {sent.isPending ? (
          <Loading label="Loading questionnaires" />
        ) : sent.isError ? (
          <ErrorState title="The questionnaires could not load" detail={describeError(sent.error)} onRetry={() => void sent.refetch()} />
        ) : sent.data.length === 0 ? (
          <EmptyState
            title="No questionnaires sent yet"
            body="Click New questionnaire to send one to a client. Their answers show here as soon as they're in."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="ledger min-w-[44rem]">
              <thead>
                <tr>
                  <th className="label">Client</th>
                  <th className="label">Questionnaire</th>
                  <th className="label">Sent</th>
                  <th className="label">Status</th>
                  <th className="label" />
                </tr>
              </thead>
              <tbody>
                {sent.data.map((row) => (
                  <QuestionnaireRow key={row.id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </Page>
  )
}

function QuestionnaireRow({ row }: { row: SentQuestionnaire }) {
  const status = questionnaireStatus(row)
  return (
    <tr>
      <td>
        <Link to={`/questionnaires/${row.id}`} className="font-semibold underline-offset-4 hover:underline">
          {clientName(row)}
        </Link>
        <span className="block text-[length:var(--text-2xs)] text-taupe">
          {row.job ? `Project · ${formatDateShort(row.job.event_date)}` : 'No project'}
        </span>
      </td>
      <td className="text-taupe">{row.name}</td>
      <td className="whitespace-nowrap text-taupe">{row.sent_at ? formatDateShort(row.sent_at.slice(0, 10)) : '—'}</td>
      <td className="whitespace-nowrap">
        <Badge tone={STATUS_TONE[status]}>
          {`${QUESTIONNAIRE_STATUS_LABEL[status]}${row.completed_at ? ` ${formatDateShort(row.completed_at.slice(0, 10))}` : ''}`}
        </Badge>
      </td>
      <td>
        <div className="flex justify-end gap-1">
          <Link
            to={`/questionnaires/${row.id}`}
            className="inline-flex h-7 items-center rounded-md bg-[var(--color-wash-strong)] px-2.5 text-[length:var(--text-xs)] font-medium hover:bg-[var(--color-rule)]"
          >
            View
          </Link>
          <Button size="sm" variant="ghost" onClick={() => void openQuestionnairePdf(row.id)}>
            Download PDF
          </Button>
        </div>
      </td>
    </tr>
  )
}
